import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/lib/analytics';

// All Google tag IDs installed in index.html. Keep in sync.
const GOOGLE_TAG_IDS = [
  'G-SXKJXYG00X',
  'G-E099VSH95D',
  'AW-18336379055',
  'GT-TX26VRXL',
  'GT-MK4HQLS5',
] as const;

/**
 * Hook to track page views on route changes
 * Add this to App.tsx inside BrowserRouter
 *
 * Fires:
 *  1. Internal analytics (Supabase + console/GA4 wrapper) via trackPageView
 *  2. gtag('event', 'page_view', { send_to: <id> }) for every configured
 *     Google tag (GA4, Ads, GT containers) so SPA route changes are counted
 *  3. dataLayer push for GTM containers (GTM-TNFCC6RV, GTM-KLPBB4, GTM-PGVR5L8G)
 */
export function usePageTracking(): void {
  const location = useLocation();

  useEffect(() => {
    const getPageTitle = (): string => {
      if (document.title && document.title !== 'Vite + React + TS') {
        return document.title;
      }
      const path = location.pathname;
      if (path === '/') return 'Home';
      return path
        .slice(1)
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    // Never send report access tokens to analytics.
    const path = location.pathname + location.search.replace(/([?&]token=)[^&#]*/, '$1redacted');
    const title = getPageTitle();
    const url = window.location.origin + path;

    // 1. Internal analytics wrapper
    trackPageView(path, title);

    // 2. Fire an explicit page_view for every Google tag. Because each tag
    //    was configured on initial load, we send targeted page_view events
    //    on subsequent SPA navigations so GA4 / Ads / GT all count them.
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: unknown[];
    };
    if (typeof w.gtag === 'function') {
      GOOGLE_TAG_IDS.forEach((id) => {
        // Update the tag's page context so subsequent events attribute correctly
        w.gtag!('config', id, {
          page_path: path,
          page_title: title,
          page_location: url,
          send_page_view: false,
        });
        // Then explicitly count this navigation
        w.gtag!('event', 'page_view', {
          send_to: id,
          page_path: path,
          page_title: title,
          page_location: url,
        });
      });
    }

    // 3. GTM: push a virtual pageview so any Tag Manager triggers listening
    //    for route changes can fire.
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({
        event: 'virtualPageview',
        page: {
          path,
          title,
          location: url,
        },
      });
    }
  }, [location.pathname, location.search]);
}
